import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import ConfirmationModal from './ConfirmationModal';
import QuizGradingDialog from './QuizGradingDialog';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Divider,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  Checkbox,
  FormControlLabel,
  FormGroup,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Quiz as QuizIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RegenerateIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CorrectIcon,
  RadioButtonUnchecked as OptionIcon,
  TextFields as ClozeIcon,
  ShortText as ShortAnswerIcon,
  Abc as VocabIcon,
  FormatListNumbered as MCIcon,
  AutoFixHigh as GenerateIcon,
  PictureAsPdf as PdfIcon,
  FactCheck as AnswerKeyIcon,
} from '@mui/icons-material';

interface Document {
  id: number;
  name: string;
  language: string;
  reading_level?: number;
  grade_level?: string;
  gcs_path?: string;
}

interface TailoredVersion {
  id: number;
  name: string;
  student_name: string;
  target_language?: string;
  target_reading_level?: number;
  target_grade_level?: string;
  tailoring_summary: string;
}

interface QuizQuestion {
  id: number;
  quiz_id: number;
  question_type: string;
  question_text: string;
  options: { label: string; text: string }[] | null;
  correct_answer: string | null;
  rubric: string | null;
  explanation: string | null;
  points_possible: number;
  order_index: number;
  is_active: boolean;
}

interface Quiz {
  id: number;
  document_id: number;
  tailored_document_id: number | null;
  title: string;
  language: string;
  reading_level: number | null;
  grade_level: string | null;
  questions_count: number;
  questions: QuizQuestion[];
  created_at: string;
}

interface QuizDialogProps {
  open: boolean;
  onClose: () => void;
  document: Document | null;
}

const QUESTION_TYPE_ICONS: Record<string, React.ReactNode> = {
  multiple_choice: <MCIcon fontSize="small" />,
  short_answer: <ShortAnswerIcon fontSize="small" />,
  cloze: <ClozeIcon fontSize="small" />,
  vocabulary: <VocabIcon fontSize="small" />,
};

const QUESTION_TYPE_COLORS: Record<string, string> = {
  multiple_choice: '#1565c0',
  short_answer: '#2e7d32',
  cloze: '#e65100',
  vocabulary: '#6a1b9a',
};

const QuizDialog: React.FC<QuizDialogProps> = ({ open, onClose, document }) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const isMobile = useMediaQuery('(max-width: 768px)');

  // State
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [tailoredVersions, setTailoredVersions] = useState<TailoredVersion[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<number | 'original'>('original');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Generation options
  const [numQuestions, setNumQuestions] = useState(8);
  const [questionTypes, setQuestionTypes] = useState({
    multiple_choice: true,
    short_answer: true,
    cloze: true,
    vocabulary: true,
  });

  // Edit state
  const [editingQuestionId, setEditingQuestionId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<QuizQuestion>>({});

  // Regenerating single question
  const [regeneratingId, setRegeneratingId] = useState<number | null>(null);

  // Publish state
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishQuizId, setPublishQuizId] = useState<number | null>(null);
  const [publishClasses, setPublishClasses] = useState<{ id: number; name: string; student_count: number }[]>([]);
  const [publishSelectedClass, setPublishSelectedClass] = useState<number | ''>('');
  const [publishClassStudents, setPublishClassStudents] = useState<{ id: number; first_name: string; last_name: string; grade_level?: string }[]>([]);
  const [publishSelectedStudents, setPublishSelectedStudents] = useState<number[]>([]);
  const [publishStudentSearch, setPublishStudentSearch] = useState('');
  const [publishDueDate, setPublishDueDate] = useState('');
  const [publishRetakes, setPublishRetakes] = useState(false);
  const [publishMaxAttempts, setPublishMaxAttempts] = useState(1);
  const [publishing, setPublishing] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);

  // Grading dialog
  const [gradingDialogOpen, setGradingDialogOpen] = useState(false);
  const [gradingQaId, setGradingQaId] = useState<number | null>(null);
  const [gradingQuizTitle, setGradingQuizTitle] = useState('');

  // Published quiz assignments per quiz
  const [publishedMap, setPublishedMap] = useState<Record<number, any[]>>({});

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    type: 'quiz' | 'question';
    id: number | null;
    name: string;
  }>({ isOpen: false, type: 'quiz', id: null, name: '' });

  // Fetch tailored versions when dialog opens
  useEffect(() => {
    if (open && document && token) {
      fetchTailoredVersions();
      setSelectedVersionId('original');
      setQuizzes([]);
      setError(null);
      setSuccessMsg(null);
    }
  }, [open, document, token]);

  // Fetch quizzes when version selection changes
  useEffect(() => {
    if (open && document && token) {
      fetchQuizzes();
    }
  }, [selectedVersionId, open, document, token]);

  const fetchTailoredVersions = async () => {
    if (!document || !token) return;
    try {
      const resp = await fetch(`/api/documents/${document.id}/tailored`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await resp.json();
      if (data.success) {
        setTailoredVersions(data.tailored_documents || []);
      }
    } catch (err) {
      console.error('Failed to load tailored versions:', err);
    }
  };

  const fetchQuizzes = async () => {
    if (!document || !token) return;
    setLoading(true);
    setError(null);
    try {
      const params = selectedVersionId !== 'original' ? `?tailored_document_id=${selectedVersionId}` : '';
      const resp = await fetch(`/api/documents/${document.id}/quizzes${params}`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await resp.json();
      if (data.success) {
        setQuizzes(data.quizzes || []);
      } else {
        setError(data.error || t('quiz.errors.fetchFailed'));
      }
    } catch (err) {
      console.error('Failed to fetch quizzes:', err);
      setError(t('quiz.errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchPublishedQuizAssignments = async () => {
    if (!document || !token) return;
    try {
      const resp = await fetch(`/api/documents/${document.id}/quiz-assignments`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await resp.json();
      if (data.success) {
        const map: Record<number, any[]> = {};
        for (const qa of data.quiz_assignments || []) {
          if (!map[qa.quiz_id]) map[qa.quiz_id] = [];
          map[qa.quiz_id].push(qa);
        }
        setPublishedMap(map);
      }
    } catch (err) {
      console.error('Failed to fetch published quiz assignments:', err);
    }
  };

  // Refresh published map when quizzes are fetched
  useEffect(() => {
    if (quizzes.length > 0 && document && token) {
      fetchPublishedQuizAssignments();
    }
  }, [quizzes.length, document, token]);

  const fetchPublishClasses = async () => {
    if (!token) return;
    try {
      const resp = await fetch('/api/classes', {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await resp.json();
      if (data.success) {
        setPublishClasses(data.classes || []);
      }
    } catch (err) {
      console.error('Failed to fetch classes:', err);
    }
  };

  const fetchClassStudents = async (classId: number) => {
    if (!token) return;
    setLoadingStudents(true);
    try {
      const resp = await fetch(`/api/classes/${classId}?include_students=true`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await resp.json();
      if (data.success && data.class?.students) {
        setPublishClassStudents(data.class.students);
        setPublishSelectedStudents(data.class.students.map((s: any) => s.id));
      }
    } catch (err) {
      console.error('Failed to fetch class students:', err);
    } finally {
      setLoadingStudents(false);
    }
  };

  const handlePointsChange = async (questionId: number, points: number) => {
    if (!token) return;
    try {
      await fetch(`/api/quiz-questions/${questionId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ points_possible: points }),
      });
      setQuizzes((prev) =>
        prev.map((quiz) => ({
          ...quiz,
          questions: quiz.questions.map((q) =>
            q.id === questionId ? { ...q, points_possible: points } : q
          ),
        }))
      );
    } catch (err) {
      console.error('Failed to update points:', err);
    }
  };

  const openPublishDialog = (quizId: number) => {
    setPublishQuizId(quizId);
    setPublishSelectedClass('');
    setPublishClassStudents([]);
    setPublishSelectedStudents([]);
    setPublishStudentSearch('');
    setPublishDueDate('');
    setPublishRetakes(false);
    setPublishMaxAttempts(1);
    setPublishDialogOpen(true);
    fetchPublishClasses();
  };

  const handlePublish = async () => {
    if (!publishQuizId || publishSelectedStudents.length === 0 || !token) return;
    setPublishing(true);
    setError(null);
    try {
      const body: Record<string, any> = {
        student_ids: publishSelectedStudents,
        allow_retakes: publishRetakes,
        max_attempts: publishMaxAttempts,
      };
      if (publishDueDate) body.due_date = publishDueDate;

      const resp = await fetch(`/api/quizzes/${publishQuizId}/publish`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (data.success) {
        setSuccessMsg(t('quiz.publishSuccess'));
        setPublishDialogOpen(false);
        fetchPublishedQuizAssignments();
      } else {
        setError(data.error || t('quiz.errors.publishFailed'));
      }
    } catch (err) {
      setError(t('quiz.errors.publishFailed'));
    } finally {
      setPublishing(false);
    }
  };

  const handleExportPdf = (quizId: number, withAnswers: boolean = false) => {
    const url = `/api/quizzes/${quizId}/export-pdf?answers=${withAnswers}`;
    const a = window.document.createElement('a');
    a.href = url;
    a.setAttribute('download', '');
    // Use fetch to add auth header
    if (token) {
      fetch(url, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.blob())
        .then((blob) => {
          const blobUrl = URL.createObjectURL(blob);
          a.href = blobUrl;
          a.click();
          URL.revokeObjectURL(blobUrl);
        })
        .catch(() => setError(t('quiz.errors.pdfFailed')));
    }
  };

  const handleGenerate = async () => {
    if (!document || !token) return;

    const activeTypes = Object.entries(questionTypes)
      .filter(([, v]) => v)
      .map(([k]) => k);

    if (activeTypes.length === 0) {
      setError(t('quiz.errors.noTypesSelected'));
      return;
    }

    setGenerating(true);
    setError(null);
    setSuccessMsg(null);

    try {
      const body: Record<string, any> = {
        num_questions: numQuestions,
        question_types: activeTypes,
      };
      if (selectedVersionId !== 'original') {
        body.tailored_document_id = selectedVersionId;
      }

      const resp = await fetch(`/api/documents/${document.id}/quizzes/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await resp.json();
      if (data.success) {
        setQuizzes((prev) => [data.quiz, ...prev]);
        setSuccessMsg(t('quiz.generateSuccess'));
      } else {
        setError(data.error || t('quiz.errors.generateFailed'));
      }
    } catch (err) {
      console.error('Quiz generation failed:', err);
      setError(t('quiz.errors.generateFailed'));
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteQuiz = async () => {
    if (deleteConfirm.type !== 'quiz' || !deleteConfirm.id || !token) return;
    const quizId = deleteConfirm.id;
    setDeleteConfirm({ isOpen: false, type: 'quiz', id: null, name: '' });

    try {
      const resp = await fetch(`/api/quizzes/${quizId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await resp.json();
      if (data.success) {
        setQuizzes((prev) => prev.filter((q) => q.id !== quizId));
        setSuccessMsg(t('quiz.deleteSuccess'));
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(t('quiz.errors.deleteFailed'));
    }
  };

  const handleDeleteQuestion = async () => {
    if (deleteConfirm.type !== 'question' || !deleteConfirm.id || !token) return;
    const questionId = deleteConfirm.id;
    setDeleteConfirm({ isOpen: false, type: 'question', id: null, name: '' });

    try {
      const resp = await fetch(`/api/quiz-questions/${questionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await resp.json();
      if (data.success) {
        setQuizzes((prev) =>
          prev.map((quiz) => ({
            ...quiz,
            questions: quiz.questions.filter((q) => q.id !== questionId),
            questions_count: quiz.questions.filter((q) => q.id !== questionId).length,
          }))
        );
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(t('quiz.errors.deleteFailed'));
    }
  };

  const handleEditStart = (question: QuizQuestion) => {
    setEditingQuestionId(question.id);
    setEditForm({
      question_text: question.question_text,
      correct_answer: question.correct_answer || '',
      rubric: question.rubric || '',
      explanation: question.explanation || '',
      options: question.options,
    });
  };

  const handleEditSave = async () => {
    if (!editingQuestionId || !token) return;
    try {
      const resp = await fetch(`/api/quiz-questions/${editingQuestionId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      const data = await resp.json();
      if (data.success) {
        setQuizzes((prev) =>
          prev.map((quiz) => ({
            ...quiz,
            questions: quiz.questions.map((q) =>
              q.id === editingQuestionId ? { ...q, ...data.question } : q
            ),
          }))
        );
        setEditingQuestionId(null);
        setEditForm({});
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(t('quiz.errors.updateFailed'));
    }
  };

  const handleRegenerate = async (questionId: number) => {
    if (!token) return;
    setRegeneratingId(questionId);
    setError(null);
    try {
      const resp = await fetch(`/api/quiz-questions/${questionId}/regenerate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await resp.json();
      if (data.success) {
        setQuizzes((prev) =>
          prev.map((quiz) => ({
            ...quiz,
            questions: quiz.questions.map((q) =>
              q.id === questionId ? { ...q, ...data.question } : q
            ),
          }))
        );
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(t('quiz.errors.regenerateFailed'));
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleClose = () => {
    setEditingQuestionId(null);
    setEditForm({});
    setError(null);
    setSuccessMsg(null);
    onClose();
  };

  const getQuestionTypeLabel = (type: string) => t(`quiz.questionTypes.${type}`, type);

  const renderQuestionCard = (question: QuizQuestion, quizIndex: number) => {
    const isEditing = editingQuestionId === question.id;
    const isRegenerating = regeneratingId === question.id;
    const typeColor = QUESTION_TYPE_COLORS[question.question_type] || '#666';

    return (
      <Box
        key={question.id}
        sx={{
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          p: 2,
          mb: 1.5,
          borderLeft: `4px solid ${typeColor}`,
          bgcolor: isEditing ? '#fafafa' : '#fff',
          opacity: isRegenerating ? 0.6 : 1,
          transition: 'all 0.2s ease',
        }}
      >
        {/* Question header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              icon={QUESTION_TYPE_ICONS[question.question_type] as React.ReactElement}
              label={getQuestionTypeLabel(question.question_type)}
              size="small"
              sx={{
                bgcolor: `${typeColor}15`,
                color: typeColor,
                fontWeight: 600,
                '& .MuiChip-icon': { color: typeColor },
              }}
            />
            <Typography variant="caption" color="text.secondary">
              #{question.order_index + 1}
            </Typography>
            {!isEditing && (
              <Tooltip title={t('quiz.pointsLabel')}>
                <TextField
                  type="number"
                  size="small"
                  value={question.points_possible}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 1;
                    handlePointsChange(question.id, val);
                  }}
                  inputProps={{ min: 0.5, step: 0.5, style: { width: 48, textAlign: 'center', padding: '2px 4px' } }}
                  sx={{ ml: 1, '& .MuiOutlinedInput-root': { height: 28 } }}
                  aria-label={t('quiz.pointsLabel')}
                />
              </Tooltip>
            )}
          </Box>

          {!isEditing && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title={t('quiz.editQuestion')}>
                <IconButton
                  size="small"
                  onClick={() => handleEditStart(question)}
                  aria-label={t('quiz.editQuestion')}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('quiz.regenerateQuestion')}>
                <IconButton
                  size="small"
                  onClick={() => handleRegenerate(question.id)}
                  disabled={isRegenerating}
                  aria-label={t('quiz.regenerateQuestion')}
                >
                  {isRegenerating ? <CircularProgress size={16} /> : <RegenerateIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title={t('quiz.deleteQuestion')}>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() =>
                    setDeleteConfirm({
                      isOpen: true,
                      type: 'question',
                      id: question.id,
                      name: question.question_text.substring(0, 60),
                    })
                  }
                  aria-label={t('quiz.deleteQuestion')}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Question body */}
        {isEditing ? (
          <Box sx={{ mt: 1 }}>
            <TextField
              label={t('quiz.questionText')}
              value={editForm.question_text || ''}
              onChange={(e) => setEditForm((prev) => ({ ...prev, question_text: e.target.value }))}
              fullWidth
              multiline
              rows={2}
              sx={{ mb: 1.5 }}
              aria-label={t('quiz.questionText')}
            />

            {question.question_type === 'multiple_choice' && editForm.options && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
                  {t('quiz.options')}
                </Typography>
                {editForm.options.map((opt, idx) => (
                  <TextField
                    key={opt.label}
                    label={`${opt.label})`}
                    value={opt.text}
                    onChange={(e) => {
                      const newOpts = [...(editForm.options || [])];
                      newOpts[idx] = { ...newOpts[idx], text: e.target.value };
                      setEditForm((prev) => ({ ...prev, options: newOpts }));
                    }}
                    fullWidth
                    size="small"
                    sx={{ mb: 0.5 }}
                    aria-label={`${t('quiz.option')} ${opt.label}`}
                  />
                ))}
              </Box>
            )}

            <TextField
              label={t('quiz.correctAnswer')}
              value={editForm.correct_answer || ''}
              onChange={(e) => setEditForm((prev) => ({ ...prev, correct_answer: e.target.value }))}
              fullWidth
              size="small"
              sx={{ mb: 1 }}
              aria-label={t('quiz.correctAnswer')}
            />

            {question.question_type === 'short_answer' && (
              <TextField
                label={t('quiz.rubric')}
                value={editForm.rubric || ''}
                onChange={(e) => setEditForm((prev) => ({ ...prev, rubric: e.target.value }))}
                fullWidth
                multiline
                rows={2}
                size="small"
                sx={{ mb: 1 }}
                aria-label={t('quiz.rubric')}
              />
            )}

            <TextField
              label={t('quiz.explanation')}
              value={editForm.explanation || ''}
              onChange={(e) => setEditForm((prev) => ({ ...prev, explanation: e.target.value }))}
              fullWidth
              multiline
              rows={2}
              size="small"
              sx={{ mb: 1.5 }}
              aria-label={t('quiz.explanation')}
            />

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                startIcon={<CancelIcon />}
                onClick={() => {
                  setEditingQuestionId(null);
                  setEditForm({});
                }}
                aria-label={t('common.cancel')}
              >
                {t('common.cancel')}
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleEditSave}
                aria-label={t('common.save')}
              >
                {t('common.save')}
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
              <ReactMarkdown>{question.question_text}</ReactMarkdown>
            </Typography>

            {/* Multiple choice options */}
            {question.question_type === 'multiple_choice' && question.options && (
              <Box sx={{ ml: 1, mb: 1 }}>
                {question.options.map((opt) => (
                  <Box
                    key={opt.label}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 0.3,
                      color:
                        opt.label === question.correct_answer ? '#2e7d32' : 'text.primary',
                      fontWeight: opt.label === question.correct_answer ? 600 : 400,
                    }}
                  >
                    {opt.label === question.correct_answer ? (
                      <CorrectIcon sx={{ fontSize: 18, color: '#2e7d32' }} />
                    ) : (
                      <OptionIcon sx={{ fontSize: 18, color: '#bbb' }} />
                    )}
                    <Typography variant="body2">
                      <strong>{opt.label})</strong> {opt.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Correct answer (non-MC) */}
            {question.question_type !== 'multiple_choice' && question.correct_answer && (
              <Box
                sx={{
                  bgcolor: '#e8f5e9',
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.75,
                  mb: 1,
                }}
              >
                <Typography variant="caption" fontWeight={600} color="#2e7d32">
                  {t('quiz.correctAnswer')}:
                </Typography>
                <Typography variant="body2" color="#2e7d32">
                  {question.correct_answer}
                </Typography>
              </Box>
            )}

            {/* Rubric */}
            {question.rubric && (
              <Box sx={{ bgcolor: '#fff3e0', borderRadius: 1, px: 1.5, py: 0.75, mb: 1 }}>
                <Typography variant="caption" fontWeight={600} color="#e65100">
                  {t('quiz.rubric')}:
                </Typography>
                <Typography variant="body2" color="#e65100">
                  {question.rubric}
                </Typography>
              </Box>
            )}

            {/* Explanation */}
            {question.explanation && (
              <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, px: 1.5, py: 0.75 }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  {t('quiz.explanation')}:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {question.explanation}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        aria-labelledby="quiz-dialog-title"
      >
        <DialogTitle
          id="quiz-dialog-title"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e0e0e0',
            pb: 2,
          }}
        >
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <QuizIcon sx={{ color: '#1565c0' }} />
              <Typography variant="h6" component="span">
                {t('quiz.title')}
              </Typography>
            </Box>
            {document && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {document.name}
              </Typography>
            )}
          </Box>
          <IconButton onClick={handleClose} size="small" aria-label={t('common.close')}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* Alerts */}
          {error && (
            <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {successMsg && (
            <Alert severity="success" sx={{ m: 2 }} onClose={() => setSuccessMsg(null)}>
              {successMsg}
            </Alert>
          )}

          {/* Version selector + generate controls */}
          <Box sx={{ p: 2, bgcolor: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
            {/* Version selector */}
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="quiz-version-label">{t('quiz.selectVersion')}</InputLabel>
              <Select
                labelId="quiz-version-label"
                value={selectedVersionId}
                onChange={(e) => setSelectedVersionId(e.target.value as number | 'original')}
                label={t('quiz.selectVersion')}
                aria-label={t('quiz.selectVersion')}
              >
                <MenuItem value="original">
                  {t('quiz.originalDocument')} ({document?.language?.toUpperCase()})
                </MenuItem>
                {tailoredVersions.map((tv) => (
                  <MenuItem key={tv.id} value={tv.id}>
                    {tv.student_name} — {tv.tailoring_summary}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Generation options */}
            <Accordion
              sx={{
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="quiz-gen-options"
                id="quiz-gen-options-header"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GenerateIcon sx={{ color: '#1565c0', fontSize: 20 }} />
                  <Typography variant="subtitle2">{t('quiz.generateNewQuiz')}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    {t('quiz.numberOfQuestions')}: <strong>{numQuestions}</strong>
                  </Typography>
                  <Slider
                    value={numQuestions}
                    onChange={(_, val) => setNumQuestions(val as number)}
                    min={4}
                    max={16}
                    step={1}
                    marks={[
                      { value: 4, label: '4' },
                      { value: 8, label: '8' },
                      { value: 12, label: '12' },
                      { value: 16, label: '16' },
                    ]}
                    sx={{ mx: 1 }}
                    aria-label={t('quiz.numberOfQuestions')}
                  />
                </Box>

                <Typography variant="body2" gutterBottom fontWeight={600}>
                  {t('quiz.questionTypesLabel')}:
                </Typography>
                <FormGroup row sx={{ mb: 2 }}>
                  {Object.entries(questionTypes).map(([key, checked]) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={(e) =>
                            setQuestionTypes((prev) => ({ ...prev, [key]: e.target.checked }))
                          }
                          size="small"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {QUESTION_TYPE_ICONS[key]}
                          <Typography variant="body2">{getQuestionTypeLabel(key)}</Typography>
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>

                <Button
                  variant="contained"
                  startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <GenerateIcon />}
                  onClick={handleGenerate}
                  disabled={generating}
                  fullWidth
                  sx={{
                    bgcolor: '#1565c0',
                    '&:hover': { bgcolor: '#0d47a1' },
                  }}
                  aria-label={t('quiz.generateQuiz')}
                >
                  {generating ? t('quiz.generating') : t('quiz.generateQuiz')}
                </Button>
              </AccordionDetails>
            </Accordion>
          </Box>

          {/* Quiz list */}
          <Box sx={{ maxHeight: isMobile ? undefined : '50vh', overflow: 'auto', p: 2 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }} color="text.secondary">
                  {t('common.loading')}
                </Typography>
              </Box>
            ) : quizzes.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <QuizIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  {t('quiz.noQuizzes')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('quiz.noQuizzesDescription')}
                </Typography>
              </Box>
            ) : (
              quizzes.map((quiz, qIdx) => (
                <Accordion
                  key={quiz.id}
                  defaultExpanded={quizzes.length === 1}
                  sx={{
                    mb: 1.5,
                    border: '1px solid #e0e0e0',
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                    borderRadius: '8px !important',
                    overflow: 'hidden',
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      '& .MuiAccordionSummary-content': {
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 1,
                        my: 0.5,
                      },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={700} noWrap>
                        {quiz.title}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                        <Chip
                          label={`${quiz.questions_count} ${t('quiz.questionsLabel')}`}
                          size="small"
                          variant="outlined"
                        />
                        {quiz.language && (
                          <Chip label={quiz.language.toUpperCase()} size="small" variant="outlined" />
                        )}
                        {quiz.grade_level && (
                          <Chip label={quiz.grade_level} size="small" variant="outlined" />
                        )}
                        <Chip
                          label={new Date(quiz.created_at).toLocaleDateString()}
                          size="small"
                          variant="outlined"
                        />
                        {publishedMap[quiz.id] && publishedMap[quiz.id].length > 0 && (
                          <Chip label={t('quiz.published')} size="small" color="success" />
                        )}
                      </Box>
                    </Box>

                    {/* Action buttons -- stopPropagation prevents accordion toggle */}
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {publishedMap[quiz.id] && publishedMap[quiz.id].length > 0 ? (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            const qa = publishedMap[quiz.id][0];
                            setGradingQaId(qa.id);
                            setGradingQuizTitle(quiz.title);
                            setGradingDialogOpen(true);
                          }}
                          aria-label={t('quiz.grading.viewResponses')}
                        >
                          {t('quiz.grading.viewResponses')}
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => openPublishDialog(quiz.id)}
                          sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                          aria-label={t('quiz.publishQuiz')}
                        >
                          {t('quiz.publishQuiz')}
                        </Button>
                      )}
                      <Tooltip title={t('quiz.exportPdf')}>
                        <IconButton
                          size="small"
                          onClick={() => handleExportPdf(quiz.id, false)}
                          aria-label={t('quiz.exportPdf')}
                        >
                          <PdfIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('quiz.exportPdfWithAnswers')}>
                        <IconButton
                          size="small"
                          onClick={() => handleExportPdf(quiz.id, true)}
                          aria-label={t('quiz.exportPdfWithAnswers')}
                          sx={{ color: '#2e7d32' }}
                        >
                          <AnswerKeyIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('quiz.deleteQuiz')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            setDeleteConfirm({
                              isOpen: true,
                              type: 'quiz',
                              id: quiz.id,
                              name: quiz.title,
                            })
                          }
                          aria-label={t('quiz.deleteQuiz')}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                    {quiz.questions.map((q) => renderQuestionCard(q, qIdx))}
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {quizzes.length > 0 &&
              t('quiz.quizCount', { count: quizzes.length })}
          </Typography>
          <Button onClick={handleClose} aria-label={t('common.close')}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Grading Dialog */}
      <QuizGradingDialog
        open={gradingDialogOpen}
        onClose={() => setGradingDialogOpen(false)}
        quizAssignmentId={gradingQaId}
        quizTitle={gradingQuizTitle}
      />

      {/* Publish Dialog */}
      <Dialog
        open={publishDialogOpen}
        onClose={() => setPublishDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="publish-quiz-dialog-title"
      >
        <DialogTitle id="publish-quiz-dialog-title">{t('quiz.publishQuiz')}</DialogTitle>
        <DialogContent>
          {/* Class selector */}
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel id="publish-class-label">{t('quiz.selectClass')}</InputLabel>
            <Select
              labelId="publish-class-label"
              value={publishSelectedClass}
              onChange={(e) => {
                const classId = e.target.value as number;
                setPublishSelectedClass(classId);
                setPublishSelectedStudents([]);
                setPublishStudentSearch('');
                if (classId) fetchClassStudents(classId);
              }}
              label={t('quiz.selectClass')}
              aria-label={t('quiz.selectClass')}
            >
              {publishClasses.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name} ({c.student_count} {t('quiz.studentsLabel')})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Student list */}
          {publishSelectedClass && (
            <>
              {loadingStudents ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : publishClassStudents.length > 0 ? (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2">
                      {t('quiz.selectStudents')} ({publishSelectedStudents.length}/{publishClassStudents.length})
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={publishSelectedStudents.length === publishClassStudents.length}
                          indeterminate={
                            publishSelectedStudents.length > 0 &&
                            publishSelectedStudents.length < publishClassStudents.length
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              setPublishSelectedStudents(publishClassStudents.map((s) => s.id));
                            } else {
                              setPublishSelectedStudents([]);
                            }
                          }}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">{t('quiz.selectAllStudents')}</Typography>}
                    />
                  </Box>

                  <TextField
                    size="small"
                    placeholder={t('common.search')}
                    value={publishStudentSearch}
                    onChange={(e) => setPublishStudentSearch(e.target.value)}
                    fullWidth
                    sx={{ mb: 1 }}
                    aria-label={t('common.search')}
                  />

                  <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    {publishClassStudents
                      .filter((s) => {
                        if (!publishStudentSearch) return true;
                        const q = publishStudentSearch.toLowerCase();
                        return (
                          s.first_name.toLowerCase().includes(q) ||
                          s.last_name.toLowerCase().includes(q)
                        );
                      })
                      .map((student) => (
                        <Box
                          key={student.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 1.5,
                            py: 0.5,
                            borderBottom: '1px solid #f0f0f0',
                            '&:hover': { bgcolor: '#f9f9f9' },
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={publishSelectedStudents.includes(student.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setPublishSelectedStudents((prev) => [...prev, student.id]);
                              } else {
                                setPublishSelectedStudents((prev) => prev.filter((id) => id !== student.id));
                              }
                            }}
                          />
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {student.first_name} {student.last_name}
                          </Typography>
                          {student.grade_level && (
                            <Chip label={student.grade_level} size="small" variant="outlined" sx={{ ml: 1 }} />
                          )}
                        </Box>
                      ))}
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('quiz.noStudentsInClass')}
                </Typography>
              )}
            </>
          )}

          <Divider sx={{ my: 1 }} />

          <TextField
            label={t('quiz.quizDueDate')}
            type="datetime-local"
            value={publishDueDate}
            onChange={(e) => setPublishDueDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2, mt: 1 }}
            aria-label={t('quiz.quizDueDate')}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={publishRetakes}
                onChange={(e) => setPublishRetakes(e.target.checked)}
              />
            }
            label={t('quiz.allowRetakes')}
          />

          {publishRetakes && (
            <TextField
              label={t('quiz.maxAttempts')}
              type="number"
              value={publishMaxAttempts}
              onChange={(e) => setPublishMaxAttempts(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: 1, max: 10 }}
              fullWidth
              sx={{ mt: 1 }}
              aria-label={t('quiz.maxAttempts')}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handlePublish}
            disabled={publishSelectedStudents.length === 0 || publishing}
            sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
            aria-label={t('quiz.publishQuiz')}
          >
            {publishing ? (
              <CircularProgress size={20} />
            ) : (
              t('quiz.publishQuizCount', { count: publishSelectedStudents.length })
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmationModal
        isOpen={deleteConfirm.isOpen}
        title={
          deleteConfirm.type === 'quiz'
            ? t('quiz.confirmDeleteQuiz')
            : t('quiz.confirmDeleteQuestion')
        }
        message={
          deleteConfirm.type === 'quiz'
            ? t('quiz.confirmDeleteQuizMessage', { name: deleteConfirm.name })
            : t('quiz.confirmDeleteQuestionMessage', { text: deleteConfirm.name })
        }
        variant="danger"
        confirmText={t('common.delete')}
        onConfirm={deleteConfirm.type === 'quiz' ? handleDeleteQuiz : handleDeleteQuestion}
        onCancel={() => setDeleteConfirm({ isOpen: false, type: 'quiz', id: null, name: '' })}
      />
    </>
  );
};

export default QuizDialog;
